import { useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  IconButton, LinearProgress, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { ItemCola, ProgresoConversion } from '../lib/api.js';
import { describirConversion, useEventos } from '../lib/eventos.js';

/** La cola de conversión, tal como está ahora mismo.
 *
 *  Vive en la memoria de la API —no hay tabla que consultar— así que se pide entera y se
 *  vuelve a pedir cuando el evento `transcode:queue` avisa de un cambio. La clave es
 *  compartida a propósito: Descargas y Conversiones enseñan lo mismo desde dos sitios y no
 *  tiene sentido que cada una traiga su copia. */
export function useColaConversion() {
  return useQuery({
    queryKey: ['cola-conversion'],
    queryFn: api.colaConversion,
    refetchInterval: 30_000,
  });
}

export const CLAVE_COLA = ['cola-conversion'] as const;

/** El botón de parar una conversión, con lo que hay que saber antes de pulsarlo.
 *
 *  Vive aquí y no en cada pantalla porque lo enseñan las dos —la cola de Conversiones y la
 *  fila de Descargas— y lo que explica es lo mismo: no se borra nada. El original de aMule
 *  sigue donde estaba y la descarga vuelve a «descargada», desde donde se convierte otra vez
 *  sin bajar un byte.
 *
 *  Se confirma solo lo que ya está bajo ffmpeg, que es lo único que cuesta algo: tirar dos
 *  horas de recodificación por un dedo torcido. Lo que espera turno no ha hecho nada
 *  todavía, y un diálogo para eso sería un paso de más en el camino de siempre. */
export function BotonCancelarConversion({ item }: { item: ItemCola }) {
  const qc = useQueryClient();
  const [confirmando, setConfirmando] = useState(false);

  const cancelar = useMutation({
    mutationFn: () => api.cancelarConversion(item.downloadId),
    // Se refresca pase lo que pase: un 409 aquí significa que la conversión terminó entre
    // que se pintó el botón y se pulsó, y entonces lo que se está mirando ya no es cierto.
    onSettled: () => {
      setConfirmando(false);
      void qc.invalidateQueries({ queryKey: CLAVE_COLA });
      void qc.invalidateQueries({ queryKey: ['descargas'] });
      void qc.invalidateQueries({ queryKey: ['conversiones'] });
    },
  });

  const enCurso = item.estado === 'en_curso';
  const nota = enCurso
    ? 'Parar la conversión. No se borra nada: el original sigue descargado y se puede ' +
      'volver a convertir sin bajar nada, pero se pierde lo que ffmpeg lleve hecho.'
    : 'Sacarla de la cola. No ha empezado a convertirse, así que no se pierde nada: queda ' +
      'como descargada y se puede convertir cuando se quiera.';

  return (
    <>
      <Tooltip title={nota}>
        {/* El `span` es para que el tooltip siga saliendo con el botón deshabilitado
            mientras la petición va: un botón apagado y mudo no dice si se pulsó. */}
        <span>
          <IconButton
            size="small"
            color="warning"
            disabled={cancelar.isPending}
            onClick={() => (enCurso ? setConfirmando(true) : cancelar.mutate())}
            aria-label="Cancelar la conversión"
          >
            <StopCircleOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog open={confirmando} onClose={() => setConfirmando(false)} fullWidth maxWidth="sm">
        <DialogTitle>Cancelar la conversión</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se para la conversión de «{item.title}». No se borra ningún fichero: el original
            descargado sigue en la carpeta de aMule y la película queda como descargada, así
            que volver a convertirla no vuelve a bajar nada. Lo que sí se pierde es el rato
            que ffmpeg lleve trabajando.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmando(false)}>Seguir convirtiendo</Button>
          <Button color="warning" disabled={cancelar.isPending} onClick={() => cancelar.mutate()}>
            Cancelar la conversión
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Qué se enseña de una película que está en la cola. Distinguir «esperando turno» de
 *  «convirtiéndose» es todo el propósito: sin ello, cinco películas detrás de una parecen
 *  cinco ffmpeg trabajando y nadie entiende por qué tardan. */
export function describirEnCola(
  item: ItemCola,
  progreso?: ProgresoConversion,
): { texto: string; nota: string } {
  if (item.estado === 'en_curso') {
    // Con el turno ya dado, lo que dice el chip es la FASE: analizando, leyendo los
    // subtítulos de imagen o bajo ffmpeg. Las tres son `transcoding` en la base de datos y
    // solo la última es de verdad «convirtiendo». En el chip cabe la etiqueta corta; el
    // detalle —cuál de cuántos, con qué motor— va debajo, en el pie de la barra.
    const fase = describirConversion(progreso);
    return { texto: fase.etiqueta, nota: fase.nota };
  }
  return {
    texto: `En cola · ${item.posicion}.ª`,
    nota:
      'Descargada y esperando turno: todavía no ha empezado a convertirse. Cuántas ' +
      'corren a la vez se ajusta en Ajustes → Conversión a MP4.',
  };
}

function FilaCola({ item }: { item: ItemCola }) {
  // Por id de descarga, no por hash: el original sigue sembrando en aMule mientras se
  // convierte, y con la clave compartida cada evento de aMule borraba este progreso.
  // Seleccionar solo el de esta película evita repintar la cola entera a cada evento.
  const progreso = useEventos((s) => s.progresoConversion[item.downloadId]);
  const estado = describirEnCola(item, progreso);
  const fase = describirConversion(progreso);

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {item.title}
          {item.year && <Typography component="span" color="text.secondary"> ({item.year})</Typography>}
        </Typography>
        <Tooltip title={estado.nota}>
          <Chip
            size="small"
            label={estado.texto}
            color={item.estado === 'en_curso' ? 'info' : 'default'}
            variant={item.estado === 'en_curso' ? 'filled' : 'outlined'}
          />
        </Tooltip>
        <BotonCancelarConversion item={item} />
      </Stack>
      {item.estado === 'en_curso' && (
        <>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 0.5 }}>
            <LinearProgress
              // Sin porcentaje —analizando, leyendo subtítulos, o los primeros segundos de
              // ffmpeg— la barra indefinida dice «está trabajando» sin inventarse un número.
              variant={fase.porcentaje == null ? 'indeterminate' : 'determinate'}
              value={fase.porcentaje ?? 0}
              sx={{ flex: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 44, textAlign: 'right' }}>
              {fase.porcentaje == null ? '—' : `${fase.porcentaje.toFixed(0)}%`}
            </Typography>
          </Stack>
          {/* El pie solo aparece cuando dice algo que la barra no puede: durante el OCR y
              el análisis no hay cifra, y sin esta línea la fila era una barra indefinida
              sin explicación durante minutos. */}
          {fase.porcentaje == null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {fase.texto}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

/** El panel de la cola. No se pinta si no hay nada: una cola vacía no es información. */
export function PanelCola() {
  const { data } = useColaConversion();
  const items = data?.items ?? [];
  if (!items.length) return null;

  const enCurso = items.filter((i) => i.estado === 'en_curso').length;
  const esperando = items.length - enCurso;

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>Cola de conversión</Typography>
        <Tooltip title="Cuántas conversiones corren a la vez. Se cambia en Ajustes → Conversión a MP4.">
          <Chip size="small" variant="outlined" label={`${enCurso} de ${data?.limite ?? 1} a la vez`} />
        </Tooltip>
      </Stack>

      <Stack spacing={1.5}>
        {items.map((i) => (
          <FilaCola key={`${i.downloadId}-${i.estado}-${i.posicion ?? 0}`} item={i} />
        ))}
      </Stack>

      {esperando > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {esperando === 1 ? 'Una película espera turno' : `${esperando} películas esperan turno`}. Van
          entrando solas conforme quede sitio; no hay nada que reintentar.
        </Typography>
      )}
    </Paper>
  );
}
